import React, { useState, useRef, useEffect } from "react";
import { Col, Row } from "reactstrap";
import UserProfileInput from "./UserProfileInput";
import UserProfileCountry from "./UserProfileCountry";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { useDispatch, useSelector } from "react-redux";
import { addPaymentCardInfo } from "../../redux/thunks/userThunks";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import "./SettingsAccountNewCard.css";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const stripeStyleOptions = {
  style: {
    base: {
      fontSize: "16px",
      color: "#1d1d1f",
      fontFamily: "inherit",
      "::placeholder": {
        color: "#aab7c4",
      },
    },
    invalid: {
      color: "#fa755a",
    },
  },
};

const SettingsAccountNewCard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    cardholderName: "",
    email: "",
    address1: "",
    address2: "",
    city: "",
    province: "",
    postal_code: "",
    country: "",
  });
  const dispatch = useDispatch();
  const stripe = useStripe();
  const elements = useElements();
  const toast = useRef(null);
  const loginUser = useSelector((state) => state.user.user.data);
  const accountAddress = loginUser?.address || "";

  useEffect(() => {
    if (loginUser) {
      setFormData((prev) => ({
        ...prev,
        cardholderName: `${loginUser.first_name} ${loginUser.last_name}`,
        email: loginUser.email,
      }));
    }
  }, [loginUser]);

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    try {
      await dispatch(
        addPaymentCardInfo(
          elements,
          formData,
          loginUser.email,
          loginUser.customer_enable,
          stripe
        )
      );
      elements.getElement(CardNumberElement).clear();
      elements.getElement(CardExpiryElement).clear();
      elements.getElement(CardCvcElement).clear();
      setFormData((prev) => ({
        ...prev,
        cardholderName: `${loginUser.first_name} ${loginUser.last_name}`,
        email: loginUser.email,
      }));

      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: "Payment card added successfully.",
      });
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to add card, please use Debit Card Only",
      });
    }
  };

  return (
    <div className="col-xl-10">
      <Toast ref={toast} />
      <div className="card">
        <div className="card-header">
          <h4 className="card-title">{t("add.payment.card")}</h4>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit} className="personal_validate">
            <div className="form-row">
              <UserProfileInput
                fieldLabel={t("card.name.label")}
                name="cardholderName"
                value={formData.cardholderName}
                onChange={handleChange("cardholderName")}
                disabled={true}
              />
              <UserProfileInput
                fieldLabel={t("address.line1.label")}
                value={formData.address1}
                onChange={handleChange("address1")}
              />
              <UserProfileInput
                fieldLabel={t("address.line2.label")}
                value={formData.address2}
                onChange={handleChange("address2")}
              />

              <Row className="align-items-center">
                <Col xs={6}>
                  <UserProfileInput
                    fieldLabel={t("city.label")}
                    value={formData.city}
                    onChange={handleChange("city")}
                  />
                </Col>
                <Col xs={6}>
                  <UserProfileInput
                    fieldLabel={t("state.label")}
                    value={formData.province}
                    onChange={handleChange("province")}
                  />
                </Col>
              </Row>

              <Row className="align-items-center">
                <Col xs={6}>
                  <UserProfileInput
                    fieldLabel={t("zip.code.label")}
                    value={formData.postal_code}
                    onChange={handleChange("postal_code")}
                  />
                </Col>
                <Col xs={6}>
                  <UserProfileCountry
                    fieldLabel={t("country.label")}
                    value={formData.country}
                    onChange={handleChange("country")}
                  />
                </Col>
              </Row>
              <div className="form-group col-12">
                <label>{t("card.number.label")}</label>
                <div className="custom-stripe-input">
                  <CardNumberElement options={stripeStyleOptions} />
                </div>
              </div>
              <Row className="align-items-center">
                <Col xs={6}>
                  <div className="form-group">
                    <label>{t("card.expiry.label")}</label>
                    <div className="custom-stripe-input">
                      <CardExpiryElement options={stripeStyleOptions} />
                    </div>
                  </div>
                </Col>
                <Col xs={6}>
                  <div className="form-group">
                    <label>{t("card.cvc.label")}</label>
                    <div className="custom-stripe-input">
                      <CardCvcElement options={stripeStyleOptions} />
                    </div>
                  </div>
                </Col>
              </Row>
              <div className="form-group col-12 mt-3">
                {accountAddress !== "" ? (
                  <button type="submit" className="btn btn-success ps-5 pe-5">
                    {t("add.label")}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn-secondary btn-block"
                    onClick={() => navigate("/settings")}
                  >
                    {t("setup.before.add.banking.label")}
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SettingsAccountNewCard;
